/**
 * @file StatementRunHistory.js
 * @version 3.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-09
 * @modified 2026-10-12
 *
 * 3.4.0: "Take over this run" (admins) opens a run on Generate Statements as
 * yours, whoever it was handed to; draft runs (handed over before generating)
 * are labelled, and closed / replaced drafts are not listed.
 */

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './SearchLayout.css';
import './StatementRunHistory.css';
import { showAlert } from './Alert';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import StatementDeliverModal from './StatementDeliverModal';
import SearchHelp from './SearchHelp';
import { matchesAllTerms } from '../utils/searchMatch';
import StatementDeliveryLog, { ResentFlag } from './StatementDeliveryLog';
import { deliveryOutcome } from '../utils/statementDelivery';
import { formatQuantity } from '../utils/numberFormatters';
import { useNavStore } from '../store/navStore';

/**
 * "Take over this run" (admins): join it (services/statementRunWorkService
 * takeOver — audited, nobody else is removed) and open it on Generate
 * Statements.
 */
async function takeOverRun(runId) {
  try {
    const res = await fetch(`${API_URL}/api/statements/work/${runId}/take-over`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ mode: 'take_over' })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok && data.code !== 'ALREADY_ON') {
      showAlert(data.error || `Could not take over the run (HTTP ${res.status})`, 'error');
      return;
    }
    useNavStore.getState().setPage('generateStatements.openRunId', Number(runId));
    window.history.pushState(null, '', '#create-statement');
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch (err) {
    showAlert(`Could not take over the run: ${err.message}`, 'error');
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatOverdueDate = (dateString) => {
  if (!dateString) return '';
  // Already pre-formatted by statementService (e.g. "AUG '25", ">12M")
  if (dateString.includes("'") || dateString.startsWith('>')) return dateString;
  const d = new Date(dateString);
  const mon = d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase();
  const yr  = String(d.getFullYear()).slice(-2);
  return `${mon} '${yr}`;
};

const formatCurrency = (amount) => {
  const num = parseFloat(amount || 0);
  return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── Delivery badge ────────────────────────────────────────────────────────────

const DeliveryBadge = ({ method }) => {
  if (!method) return <span className="delivery-badge badge-pending">Not delivered</span>;
  const config = {
    emailed_office:   { label: 'Emailed (Office)',   className: 'badge-email-office' },
    emailed_customer: { label: 'Emailed (Customer)', className: 'badge-email-customer' },
    printed:          { label: 'Printed',            className: 'badge-printed' },
    saved:            { label: 'Saved',              className: 'badge-saved' },
  };
  const c = config[method] || { label: method, className: 'badge-pending' };
  return <span className={`delivery-badge ${c.className}`}>{c.label}</span>;
};

// ── Customer detail modal ─────────────────────────────────────────────────────

const CustomerDetailModal = ({ statementId, customer, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useSlideOutButton();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/statements/admin/statements/${statementId}/data`, {
          credentials: 'include'
        });
        if (res.ok) {
          setDetail(await res.json());
        } else {
          showAlert('Failed to load statement data', 'error');
          onClose();
        }
      } catch {
        showAlert('Connection error', 'error');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [statementId, onClose]);

  const statementData = detail?.data
    ? (typeof detail.data === 'string' ? JSON.parse(detail.data) : detail.data)
    : null;

  const invoiceSnapshot = detail?.invoice_snapshot
    ? (typeof detail.invoice_snapshot === 'string' ? JSON.parse(detail.invoice_snapshot) : detail.invoice_snapshot)
    : null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl statement-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-left">
            <h2 className="modal-title">{customer.account_no ? `${customer.account_no} — ` : ''}{customer.company_name}</h2>
          </div>
          <div className="header-actions">
            <button className="btn-icon" onClick={onClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        <div className="modal-body customer-detail-body">
          {loading ? (
            <div className="detail-loading">
              <span className="material-symbols-rounded spinning">progress_activity</span>
              Loading...
            </div>
          ) : detail ? (
            <>
              {/* Delivery summary */}
              <div className="delivery-summary-wrapper">
                <div className="delivery-summary">
                  <div className="ds-item">
                    <span className="ds-label">Delivery</span>
                    <DeliveryBadge method={detail.delivery_method} />
                  </div>
                  {detail.delivered_at && (
                    <div className="ds-item">
                      <span className="ds-label">When</span>
                      <span className="ds-value">{formatDateTime(detail.delivered_at)}</span>
                    </div>
                  )}
                  {detail.recipient_email && (
                    <div className="ds-item">
                      <span className="ds-label">Sent to</span>
                      <span className="ds-value">{detail.recipient_email}</span>
                    </div>
                  )}
                  {detail.delivered_by_name && (
                    <div className="ds-item">
                      <span className="ds-label">By</span>
                      <span className="ds-value">{detail.delivered_by_name}</span>
                    </div>
                  )}
                  {detail.customer_email && (
                    <div className="ds-item">
                      <span className="ds-label">Customer email</span>
                      <span className="ds-value">{detail.customer_email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Statement transactions */}
              {statementData?.transactions?.length > 0 && (
                <div className="detail-section">
                  <h3 className="detail-section-title">Statement Transactions</h3>
                  <div className="table-wrapper">
                    <table className="data-table detail-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Activity</th>
                          <th>Reference</th>
                          <th>Due Date</th>
                          <th className="text-right">Invoice Amt</th>
                          <th className="text-right">Payments</th>
                          <th className="text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statementData.transactions.map((t, i) => (
                          <tr key={i} className={t.isBalanceForward ? 'row-balance-forward' : ''}>
                            <td>{t.date ? formatDate(t.date) : ''}</td>
                            <td>{t.activity || ''}</td>
                            <td>{t.reference || ''}</td>
                            <td>{t.dueDate ? ((t.isOverdue || t.isCredit) ? formatOverdueDate(t.dueDate) : formatDate(t.dueDate)) : ''}</td>
                            <td className="text-right">{t.amount != null ? formatCurrency(t.amount) : ''}</td>
                            <td className="text-right">{t.payment != null ? formatCurrency(t.payment) : ''}</td>
                            <td className="text-right bold">{t.balance != null ? formatCurrency(t.balance) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {statementData.totalDue != null && (
                    <div className="total-due-row">
                      <span>Statement Balance</span>
                      <span className="total-due-amount">£{formatCurrency(statementData.totalDue)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Invoice snapshot */}
              {invoiceSnapshot?.length > 0 && (
                <div className="detail-section">
                  <h3 className="detail-section-title">Invoices Included ({invoiceSnapshot.length})</h3>
                  <div className="invoice-cards">
                    {invoiceSnapshot.map((inv, i) => (
                      <div key={i} className="invoice-card">
                        <div className="invoice-card-header">
                          <span className="inv-number">{inv.invoice?.number || `Invoice ${i + 1}`}</span>
                          <span className="inv-date">{inv.invoice?.date ? formatDate(inv.invoice.date) : ''}</span>
                          <span className="inv-total">£{formatCurrency(inv.invoice?.total)}</span>
                        </div>
                        {inv.items?.length > 0 && (
                          <table className="data-table inv-items-table">
                            <thead>
                              <tr>
                                <th>Description</th>
                                <th className="text-right">Qty</th>
                                <th className="text-right">Unit</th>
                                <th className="text-right">VAT</th>
                                <th className="text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inv.items.map((item, j) => (
                                <tr key={j}>
                                  <td>{item.description}</td>
                                  <td className="text-right">{formatQuantity(item.quantity || 0)}</td>
                                  <td className="text-right">£{formatCurrency(item.unitPrice)}</td>
                                  <td className="text-right">{item.taxPercent}%</td>
                                  <td className="text-right">£{formatCurrency(item.lineTotal)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!statementData?.transactions?.length && !invoiceSnapshot?.length && (
                <div className="no-data-message">No statement or invoice data captured for this record.</div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Run detail modal ──────────────────────────────────────────────────────────

const RunDetailModal = ({ run, onClose }) => {
  useSlideOutButton();

  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [deliverStatement, setDeliverStatement] = useState(null);
  // Statements whose delivery log is open (same log as the contact modal's
  // Statements panel — every send, resend, print and save).
  const [expanded, setExpanded] = useState(() => new Set());

  // Also the refresh after a redeliver, so a resend replaces the failure flag
  // without reopening the run. Only the first load shows the spinner.
  const loadStatements = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/statements/admin/runs/${run.id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setStatements(data.statements || []);
      } else {
        showAlert('Failed to load run details', 'error');
      }
    } catch {
      showAlert('Connection error', 'error');
    } finally {
      setLoading(false);
    }
  }, [run.id]);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  const toggleLog = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Failed / bounced / delivered / resent, from each statement's delivery log
  // in order (utils/statementDelivery) — a later attempt of the same kind
  // answers a failure, so a resend to the customer clears a customer bounce.
  const outcomes = useMemo(
    () => new Map(statements.map(s => [s.id, deliveryOutcome(s.deliveries, s)])),
    [statements]
  );
  const outcomeOf = (s) => outcomes.get(s.id) || deliveryOutcome([]);

  const filtered = statements.filter(s => matchesAllTerms([s.company_name, s.account_no], customerSearch));

  const delivered = statements.filter(s => s.delivery_method);
  const emailed   = statements.filter(s => s.delivery_method?.startsWith('emailed'));
  const printed   = statements.filter(s => s.delivery_method === 'printed');
  const saved     = statements.filter(s => s.delivery_method === 'saved');
  // The statement's own status, as the run list counts it (failed_statements);
  // a successful resend that answers the refusal sets it back to 'sent'.
  const failed    = statements.filter(s => s.email_status === 'send_failed');
  const bounced   = statements.filter(s => outcomeOf(s).bounced);
  const resent    = statements.filter(s => outcomeOf(s).resent);

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl statement-run-details" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-left">
            <h2 className="modal-title">Run #{run.id} — {formatDate(run.period_start)} to {formatDate(run.period_end)}</h2>
          </div>
          <div className="header-actions">
            <button
              className="btn-icon"
              onClick={() => takeOverRun(run.id)}
              title="Take over this run — open it on Generate Statements as yours (anyone on it stays on it)"
            >
              <span className="material-symbols-rounded">assignment_ind</span>
            </button>
            <button className="btn-icon" onClick={onClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        <div className="modal-body run-detail-body">
          <div className='sticky'>
            
            {/* Run summary */}
            <div className="run-summary">
              <div className="summary-row">
                <span className="label">Created:</span>
                <span className="value">{formatDateTime(run.created_at)}</span>
              </div>
              <div className="summary-row">
                <span className="label">Created by:</span>
                <span className="value">{run.created_by_name || '—'}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="stats-row">
              <div className="stat-box">
                <span className="stat-value">{statements.length}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat-box stat-success">
                <span className="stat-value">{delivered.length}</span>
                <span className="stat-label">Delivered</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{emailed.length}</span>
                <span className="stat-label">Emailed</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{printed.length}</span>
                <span className="stat-label">Printed</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{saved.length}</span>
                <span className="stat-label">Saved</span>
              </div>
              {failed.length > 0 && (
                <div className="stat-box stat-error">
                  <span className="stat-value">{failed.length}</span>
                  <span className="stat-label">Failed</span>
                </div>
              )}
              {bounced.length > 0 && (
                <div className="stat-box stat-error">
                  <span className="stat-value">{bounced.length}</span>
                  <span className="stat-label">Bounced</span>
                </div>
              )}
              {resent.length > 0 && (
                <div className="stat-box stat-success" title="Failed or bounced, then sent again">
                  <span className="stat-value">{resent.length}</span>
                  <span className="stat-label">Resent</span>
                </div>
              )}
            </div>

            {/* Customer search */}
            <div className="customer-search-bar">
              <span className="material-symbols-rounded search-icon">search</span>
              <input
                type="text"
                placeholder="Search customer name or account number..."
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                className="customer-search-input"
                autoFocus
              />
              <SearchHelp fields={['customer', 'account no.']} />
              {customerSearch && (
                <button className="btn-icon" onClick={() => setCustomerSearch('')} title="Clear">
                  <span className="material-symbols-rounded">close</span>
                </button>
              )}
            </div>
          </div>

          {/* Statements table */}
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Customer</th>
                  <th>Delivery</th>
                  <th>When</th>
                  <th>By</th>
                  <th>Recipient</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="loading-cell">
                      <span className="material-symbols-rounded spinning">progress_activity</span>
                      Loading...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-cell">
                      {customerSearch ? 'No matching customers' : 'No statements in this run'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => {
                    const o = outcomeOf(s);
                    const isOpen = expanded.has(s.id);
                    return (
                      <Fragment key={s.id}>
                        <tr className="expandable-row" onClick={() => toggleLog(s.id)}>
                          <td><strong>{s.account_no}</strong></td>
                          <td>{s.company_name}</td>
                          <td>
                            <DeliveryBadge method={s.delivery_method} />
                            {/* Both flags when both apply, as in the contact modal. */}
                            {(o.bounced || o.failed) && (
                              <span className="material-symbols-rounded deliv-warn" role="img"
                                title={o.bounced ? 'Bounced or marked as spam' : 'A delivery failed'}
                                aria-label={o.bounced ? 'Bounced or marked as spam' : 'A delivery failed'}>error</span>
                            )}
                            <ResentFlag outcome={o} />
                            {!o.bounced && !o.failed && !o.resent && o.delivered && (
                              <span className="material-symbols-rounded deliv-ok" role="img"
                                title="Delivered to recipient" aria-label="Delivered to recipient">check_circle</span>
                            )}
                          </td>
                          <td>{formatDateTime(s.delivered_at)}</td>
                          <td>{s.delivered_by_name || '—'}</td>
                          <td>{s.recipient_email || '—'}</td>
                          <td className="actions-col">
                            <div className="actions-cell">
                              <button
                                className="btn-icon"
                                onClick={(e) => { e.stopPropagation(); toggleLog(s.id); }}
                                title={isOpen ? 'Hide delivery log' : 'Delivery log'}
                              >
                                <span className="material-symbols-rounded">{isOpen ? 'expand_less' : 'history'}</span>
                              </button>
                              <button
                                className="btn-icon"
                                onClick={(e) => { e.stopPropagation(); setSelectedStatement(s); }}
                                title="View Statement & Invoices"
                              >
                                <span className="material-symbols-rounded">visibility</span>
                              </button>
                              <button
                                className="btn-icon"
                                onClick={(e) => { e.stopPropagation(); setDeliverStatement(s); }}
                                title="Regenerate / preview / redeliver"
                              >
                                <span className="material-symbols-rounded">send</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr className={`expanded-content ${isOpen ? 'expanded' : ''}`}>
                          <td colSpan={7}>
                            <div className="line-items-container">
                              <StatementDeliveryLog deliveries={s.deliveries} />
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedStatement && (
        <CustomerDetailModal
          statementId={selectedStatement.id}
          customer={selectedStatement}
          onClose={() => setSelectedStatement(null)}
        />
      )}

      {deliverStatement && (
        <StatementDeliverModal
          statement={{
            id: deliverStatement.id,
            account_no: deliverStatement.account_no,
            company_name: deliverStatement.company_name,
            customer_email: deliverStatement.customer_email || deliverStatement.recipient_email
          }}
          onClose={() => { setDeliverStatement(null); loadStatements(); }}
          onDelivered={loadStatements}
        />
      )}
    </div>,
    document.body
  );
};

// ── Main component ────────────────────────────────────────────────────────────

function StatementRunHistory() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);

  // Searchable dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [runSearch, setRunSearch] = useState('');
  const dropdownRef = useRef(null);

  useSlideOutButton();

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/statements/admin/runs?page=1&limit=200`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setRuns(data.runs);
      } else {
        showAlert('Failed to load statement runs', 'error');
      }
    } catch {
      showAlert('Connection error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredRuns = runs.filter(r => matchesAllTerms(
    [`#${r.id}`, formatDate(r.period_start), '–', formatDate(r.period_end), r.created_by_name],
    runSearch
  ));

  const handleSelectRun = (run) => {
    setSelectedRun(run);
    setDropdownOpen(false);
    setRunSearch('');
  };

  const runLabel = (run) =>
    `Run #${run.id} — ${formatDate(run.period_start)} to ${formatDate(run.period_end)}`;

  return (
    <div className="statement-run-history">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Select a statement run to view delivery history and customer details.</span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button onClick={fetchRuns} className="btn-slideout" disabled={loading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">refresh</span>
                </div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
          <div className="search-stats">
            {loading ? 'Loading...' : `${runs.length} run${runs.length !== 1 ? 's' : ''}`}
          </div>
        </div>
      </div>

      {/* Run selector */}
      <div className="run-selector-container">
        <div className="run-selector" ref={dropdownRef}>
          <div
            className={`run-selector-trigger ${dropdownOpen ? 'open' : ''}`}
            onClick={() => setDropdownOpen(v => !v)}
          >
            <span className="material-symbols-rounded selector-icon">receipt</span>
            <span className="selector-label">
              {selectedRun ? runLabel(selectedRun) : 'Select a statement run…'}
            </span>
            <span className="material-symbols-rounded selector-chevron">expand_more</span>
          </div>

          {dropdownOpen && (
            <div className="run-dropdown">
              <div className="run-dropdown-search">
                <span className="material-symbols-rounded">search</span>
                <input
                  type="text"
                  placeholder="Search runs…"
                  value={runSearch}
                  onChange={e => setRunSearch(e.target.value)}
                  autoFocus
                  onClick={e => e.stopPropagation()}
                />
                <SearchHelp fields={['run no. (#12)', 'period dates', 'created by']} />
              </div>
              <div className="run-dropdown-list">
                {loading ? (
                  <div className="run-dropdown-empty">Loading…</div>
                ) : filteredRuns.length === 0 ? (
                  <div className="run-dropdown-empty">No runs found</div>
                ) : (
                  filteredRuns.map(run => (
                    <div
                      key={run.id}
                      className={`run-dropdown-item ${selectedRun?.id === run.id ? 'active' : ''}`}
                      onClick={() => handleSelectRun(run)}
                    >
                      <div className="rdi-main">
                        <strong>Run #{run.id}</strong>
                        <span className="rdi-period">{formatDate(run.period_start)} – {formatDate(run.period_end)}</span>
                      </div>
                      <div className="rdi-meta">
                        <span>{run.created_by_name || '—'}</span>
                        <span className="rdi-counts">
                          {run.status === 'draft' ? 'Draft — handed over before generating' : `${run.total_statements} stmts`}
                          {parseInt(run.sent_statements) > 0 && ` · ${run.sent_statements} sent`}
                          {parseInt(run.printed_statements) > 0 && ` · ${run.printed_statements} printed`}
                          {parseInt(run.saved_statements) > 0 && ` · ${run.saved_statements} saved`}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Placeholder when no run selected */}
      {!selectedRun && !loading && (
        <div className="no-run-selected">
          <span className="material-symbols-rounded no-run-icon">receipt</span>
          <p>Select a statement run above to view its history</p>
        </div>
      )}

      {/* Run detail modal */}
      {selectedRun && (
        <RunDetailModal
          run={selectedRun}
          onClose={() => setSelectedRun(null)}
        />
      )}
    </div>
  );
}

export default StatementRunHistory;
